import { Eye, EyeOff } from "lucide-react";
import React, { useState } from "react";

const getAdornment = (InputProps = {}, key) => InputProps?.[key] || null;

export const InputTextField = ({
  label,
  type = "text",
  name,
  value,
  onChange,
  error,
  helperText,
  required = false,
  options = [],
  showPassword,
  onTogglePassword,
  InputProps,
  inputProps,
  select,
  id,
  ...otherProps
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(Boolean(showPassword));
  const inputId = id || name || label;
  const isSelect = select || type === "select";
  const isPassword = type === "password";
  const startAdornment = getAdornment(InputProps, "startAdornment");
  const endAdornment = getAdornment(InputProps, "endAdornment");
  const fieldProps = { ...otherProps };

  [
    "className",
    "customInput",
    "format",
    "fullWidth",
    "mask",
    "margin",
    "variant"
  ].forEach((key) => {
    delete fieldProps[key];
  });

  const handleClickShowPassword = () => {
    setIsPasswordVisible((prev) => !prev);
    onTogglePassword?.();
  };

  
  const mergedInputStyle = {
    ...(inputProps?.style || {}),
    ...(fieldProps?.style || {}),
    ...(startAdornment ? { paddingLeft: "5rem" } : {}),
  };

  const sharedClassName = `block h-12 w-full rounded-md border bg-white pr-3 py-3 text-left text-sm text-gray-700 shadow-sm outline-none transition focus:ring-2 ${
    startAdornment ? "pl-20" : "pl-3"
  } ${
    endAdornment || isPassword ? "pr-12" : ""
  } ${
    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
  }`;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="relative">
        {startAdornment && (
          <span className="absolute inset-y-0 left-3 inline-flex w-12 items-center justify-center text-sm font-medium text-gray-500">
            {startAdornment}
          </span>
        )}
        {isSelect ? (
          <select
            id={inputId}
            name={name}
            className={sharedClassName}
            value={value || ""}
            onChange={onChange}
            required={required}
            {...inputProps}
            {...fieldProps}
          >
            {options.map((option, index) => (
              <option key={option.value ?? index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={inputId}
            name={name}
            className={sharedClassName}
            type={isPassword && !isPasswordVisible ? "password" : isPassword ? "text" : type}
            value={value || ""}
            onChange={onChange}
            required={required}
            autoComplete="off"
            {...inputProps}
            {...fieldProps}
            style={mergedInputStyle}
          />
        )}
        {isPassword ? (
          <button
            type="button"
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-2 inline-flex items-center justify-center rounded px-2 text-gray-500 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            onClick={handleClickShowPassword}
          >
            {isPasswordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        ) : endAdornment ? (
          <span className="absolute inset-y-0 right-3 inline-flex items-center text-gray-500">
            {endAdornment}
          </span>
        ) : null}
      </div>
      {(helperText || error) && (
        <div className={`mt-1 text-xs ${error ? "text-red-500" : "text-gray-500"}`}>
          {helperText || error}
        </div>
      )}
    </div>
  );
};
